import * as Yup from 'yup';

import { CurrencyCode } from 'types/currency';

import { makeLabels } from 'utils/i18n';

export const FORM_FIELDS = {
  CURRENCY: 'fiatCurrencyCode',
  FIAT_AMOUNT: 'fiatAmount',
  EXTERNAL_PAYOUT_ID: 'externalPayoutId',
  EMAIL: 'email',
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  DOB: 'dob',
  COUNTRY_OF_RESIDENCE: 'countryOfResidence',
} as const;

export type FormFieldType = (typeof FORM_FIELDS)[keyof typeof FORM_FIELDS];
type FormLabels = Record<FormFieldType, [string, string]>;

export const CUSTOMER_BASE_FIELDS = [
  FORM_FIELDS.EMAIL,
  FORM_FIELDS.FIRST_NAME,
  FORM_FIELDS.LAST_NAME,
  FORM_FIELDS.DOB,
  FORM_FIELDS.COUNTRY_OF_RESIDENCE,
] as const;

const FORM_LABELS: FormLabels = {
  [FORM_FIELDS.CURRENCY]: ['currency', 'Currency'],
  [FORM_FIELDS.FIAT_AMOUNT]: ['fiatAmount', 'Amount'],
  [FORM_FIELDS.EXTERNAL_PAYOUT_ID]: ['externalPayoutId', 'External Payout ID'],
  [FORM_FIELDS.EMAIL]: ['email', 'Email'],
  [FORM_FIELDS.FIRST_NAME]: ['firstName', 'First Name'],
  [FORM_FIELDS.LAST_NAME]: ['lastName', 'Last Name'],
  [FORM_FIELDS.DOB]: ['dob', 'Date of Birth'],
  [FORM_FIELDS.COUNTRY_OF_RESIDENCE]: ['countryOfResidence', 'Country of Residence'],
};

const FORM_PLACEHOLDERS: FormLabels = {
  [FORM_FIELDS.CURRENCY]: ['currencyPlaceholder', ''],
  [FORM_FIELDS.FIAT_AMOUNT]: ['fiatAmountPlaceholder', 'Total Amount'],
  [FORM_FIELDS.EXTERNAL_PAYOUT_ID]: ['externalPayoutIdPlaceholder', '0000-0000-0000-0000'],
  [FORM_FIELDS.EMAIL]: ['emailPlaceholder', 'Enter Customer Email'],
  [FORM_FIELDS.FIRST_NAME]: ['firstNamePlaceholder', 'Enter First Name'],
  [FORM_FIELDS.LAST_NAME]: ['lastNamePlaceholder', 'Enter Last Name'],
  [FORM_FIELDS.DOB]: ['dobPlaceholder', 'YYYY-MM-DD'],
  [FORM_FIELDS.COUNTRY_OF_RESIDENCE]: ['countryOfResidencePlaceholder', 'Select a Country'],
};

export const FORM_INITIAL_VALUES: Record<FormFieldType, string> = {
  [FORM_FIELDS.CURRENCY]: CurrencyCode.USD,
  [FORM_FIELDS.FIAT_AMOUNT]: '',
  [FORM_FIELDS.EXTERNAL_PAYOUT_ID]: '',
  [FORM_FIELDS.EMAIL]: '',
  [FORM_FIELDS.FIRST_NAME]: '',
  [FORM_FIELDS.LAST_NAME]: '',
  [FORM_FIELDS.DOB]: '',
  [FORM_FIELDS.COUNTRY_OF_RESIDENCE]: '',
};

export const VALIDATION_SCHEMA = Yup.object().shape({
  [FORM_FIELDS.CURRENCY]: Yup.string().required('Currency is required'),
  [FORM_FIELDS.FIAT_AMOUNT]: Yup.number()
    .required('Amount is required')
    .positive('Amount must be positive')
    .typeError('Amount must be a number'),
  [FORM_FIELDS.EXTERNAL_PAYOUT_ID]: Yup.string(),
  [FORM_FIELDS.EMAIL]: Yup.string().required('Email is required').email('Invalid email address'),
  [FORM_FIELDS.FIRST_NAME]: Yup.string().required('First name is required').min(2, 'First name must be at least 2 characters'),
  [FORM_FIELDS.LAST_NAME]: Yup.string().required('Last name is required').min(2, 'Last name must be at least 2 characters'),
  [FORM_FIELDS.DOB]: Yup.string()
    .required('Date of birth is required')
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
  [FORM_FIELDS.COUNTRY_OF_RESIDENCE]: Yup.string().required('Country of residence is required'),
});

const NAMESPACE = 'page.cryptoPayout.form';
export const LABELS = makeLabels(
  {
    TAX_REFUND_PANEL_TITLE: ['taxRefundPanelTitle', 'Tax Refund'],
    ORDER_SUMMARY_PANEL_TITLE: ['orderSummaryPanelTitle', 'Order Summary'],
    CUSTOMER_DETAILS_PANEL_TITLE: ['customerDetailsPanelTitle', 'Customer Details'],
    FORM_FIELDS: {
      LABELS: FORM_LABELS,
      PLACEHOLDERS: FORM_PLACEHOLDERS,
    },
  },
  NAMESPACE,
);

export default LABELS;
